import { useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { analytics, capPeriodId } from '@/lib/analytics';

export type CapKind = 'ai_message' | 'saves' | 'mcp_call';

interface CapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: CapKind | null;
  used?: number;
  limit?: number;
  upgradeUrl?: string;
}

const COPY: Record<CapKind, { title: string; body: (used?: number, limit?: number) => string }> = {
  ai_message: {
    title: 'You\'ve hit your monthly chat limit',
    body: (used, limit) =>
      limit
        ? `You\'ve used all ${limit} chat messages this month on the Saver plan. Pro gets you 500/month — enough headroom for daily use.`
        : 'You\'ve hit your monthly chat limit. Upgrade to Pro for 500 messages/month.',
  },
  saves: {
    title: 'Your save list is full',
    body: (used, limit) =>
      limit
        ? `Saver tier holds up to ${limit} saves. You\'re at ${used ?? limit}. Delete a page to free space, or upgrade to Pro for unlimited saves.`
        : 'You\'ve hit the Saver save limit. Delete a page or upgrade to Pro for unlimited saves.',
  },
  mcp_call: {
    title: 'You\'ve hit your monthly MCP limit',
    body: (used, limit) =>
      limit
        ? `Saver tier allows ${limit} MCP calls per month. You\'re at ${used ?? limit}. Pro removes the cap entirely.`
        : 'You\'ve hit your monthly MCP call limit. Upgrade to Pro for unlimited calls.',
  },
};

export function CapModal({ open, onOpenChange, kind, used, limit, upgradeUrl }: CapModalProps) {
  const wasOpenRef = useRef(false);
  const dismissedNaturallyRef = useRef(true);

  useEffect(() => {
    if (!kind) return;
    if (open && !wasOpenRef.current) {
      wasOpenRef.current = true;
      dismissedNaturallyRef.current = true; // until proven otherwise
      analytics.track('cap_modal_viewed', { cap_type: kind, used, limit, cap_period_id: capPeriodId() });
    } else if (!open && wasOpenRef.current) {
      wasOpenRef.current = false;
      // dismissed_natural=true means user closed via "Not now" / overlay / ESC,
      // not by clicking Upgrade (which sets dismissedNaturallyRef.current=false).
      if (dismissedNaturallyRef.current) {
        analytics.track('cap_modal_dismissed', { cap_type: kind, used, limit });
      }
    }
  }, [open, kind, used, limit]);

  if (!kind) return null;
  const { title, body } = COPY[kind];
  const href = upgradeUrl || '/upgrade';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body(used, limit)}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          <Button
            onClick={() => {
              dismissedNaturallyRef.current = false;
              const trigger: 'cap_modal_saves' | 'cap_modal_chat' | 'cap_modal_mcp' =
                kind === 'saves' ? 'cap_modal_saves'
                : kind === 'ai_message' ? 'cap_modal_chat'
                : 'cap_modal_mcp';
              const id = analytics.beginUpgradeIntent(trigger, {
                cap_type: kind,
                used,
                limit,
                destination_path: '/upgrade',
              });
              const url = href.includes('?')
                ? `${href}&upgrade_intent_id=${id}&from=${trigger}`
                : `${href}?upgrade_intent_id=${id}&from=${trigger}`;
              window.location.assign(url);
            }}
          >
            Upgrade to Pro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Parse a chat /api/chat 402 error into CapModal props. Returns null if the
 * error isn't a cap denial. Used by ConversationSession to surface the modal
 * when `useChat` exposes the error.
 *
 * The AI SDK surfaces the response body as the Error message in most builds;
 * this matches both the JSON-string form and the raw sentinel form.
 */
export function parseChatCapError(message: string | null | undefined): null | {
  kind: CapKind;
  used?: number;
  limit?: number;
  upgrade_url?: string;
} {
  if (!message) return null;
  if (!message.includes('solem_chat_cap_exceeded')) return null;
  try {
    // Body might be wrapped — try to find the JSON object inside the message.
    const jsonStart = message.indexOf('{');
    if (jsonStart >= 0) {
      const payload = JSON.parse(message.slice(jsonStart));
      return {
        kind: 'ai_message',
        used: payload.used,
        limit: payload.limit,
        upgrade_url: payload.upgrade_url,
      };
    }
  } catch {
    // Fall through — return generic verdict
  }
  return { kind: 'ai_message' };
}
